import axios from "axios";
import { createSignal } from "solid-js";
import { toast } from "solid-sonner";
import { useI18n } from "@/api/i18n";
import { getAssetDownloadUrl } from "@/domains/documents/api";

type UseAssetDownloadOptions = {
  /**
   * Navigation strategy toward the signed URL. Defaults to same-tab
   * `window.location.assign` — a `Content-Disposition: attachment` response is
   * handed to the browser download manager and does NOT unload the SPA. Never
   * use `window.open` after an await (mobile popup blockers kill it).
   * Injectable for tests.
   */
  navigate?: (url: string) => void;
};

/**
 * Imperative, per-asset download of a private asset via signed URL.
 *
 * - `download(assetId)`: fetches a fresh signed URL at click time and navigates
 *   to it. No-op while a request for the same id is in flight (dedupe). Parallel
 *   downloads of different ids are allowed. The signed URL is never persisted.
 * - `isPending(assetId)`: per-asset pending flag — bind to `disabled` + `aria-busy`.
 *
 * Error handling: 404 → neutral toast (stays clickable); 401 → silent (the axios
 * interceptor owns session refresh/redirect); anything else → generic error toast.
 */
export const useAssetDownload = (options?: UseAssetDownloadOptions) => {
  const { t } = useI18n();
  const [pending, setPending] = createSignal<ReadonlySet<string>>(new Set());

  const navigate =
    options?.navigate ?? ((url: string) => window.location.assign(url));

  const isPending = (assetId: string) => pending().has(assetId);

  const download = async (assetId: string) => {
    if (pending().has(assetId)) return;
    setPending((prev) => new Set(prev).add(assetId));

    try {
      const { url } = await getAssetDownloadUrl(assetId);
      navigate(url);
    } catch (error) {
      const status = axios.isAxiosError(error)
        ? error.response?.status
        : undefined;
      if (status === 401) {
        // Session handled by the axios interceptor (refresh + retry, else
        // redirect to /auth/logout). The document toast would be noise.
        return;
      }
      if (status === 404) {
        toast(t("documents.download.unavailable"));
        return;
      }
      toast.error(t("common.error"));
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(assetId);
        return next;
      });
    }
  };

  return { download, isPending };
};
